<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">
            {{ isEditing ? `Ticket: ${form.numero || "-"}` : "Abrir Ticket" }}
          </h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            {{
              isEditing
                ? "Consulte os dados do ticket e acompanhe a conversa com comentários."
                : "Descreva o problema para abrir um novo ticket de suporte."
            }}
          </p>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row">
          <router-link
            to="/suporte"
            class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
          >
            <svg
              class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar
          </router-link>
        </div>
      </div>

      <form id="ticket-form" class="space-y-6" autocomplete="off" @submit.prevent="save">
        <div
          class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
        >
          <div class="border-b border-gray-100 px-6 py-5">
            <div
              class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
            >
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Informações do Ticket</h2>
                <p class="mt-1 max-w-2xl text-sm text-gray-500">
                  Preencha os dados principais do atendimento e defina categoria,
                  prioridade e descrição do problema.
                </p>
              </div>

              <p
                class="max-w-md rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-500 shadow-sm"
              >
                Use um título claro e uma descrição detalhada para facilitar a triagem e
                agilizar o atendimento.
              </p>
            </div>
          </div>

          <div class="p-6">
            <div class="grid grid-cols-12 gap-6">
              <div
                class="col-span-12 min-w-0 rounded-3xl border border-gray-100 bg-slate-50/70 p-5 md:col-span-7 md:p-6"
              >
                <div class="grid grid-cols-1 gap-5">
                  <div class="flex flex-col gap-1.5">
                    <label for="titulo" class="text-sm font-medium text-gray-500">
                      Título*
                    </label>
                    <input
                      id="titulo"
                      v-model="form.titulo"
                      :disabled="isEditing"
                      type="text"
                      maxlength="80"
                      placeholder="Escreva um breve resumo do problema"
                      :class="inputClass"
                    />
                  </div>

                  <div class="flex flex-col gap-1.5">
                    <label for="descricao" class="text-sm font-medium text-gray-500">
                      Descrição*
                    </label>
                    <textarea
                      id="descricao"
                      v-model="form.descricao"
                      :disabled="isEditing"
                      rows="7"
                      placeholder="Descreva o problema com o máximo de detalhes possível."
                      :class="textareaClass"
                    />
                  </div>
                </div>
              </div>

              <div
                class="col-span-12 min-w-0 rounded-3xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm md:col-span-5"
              >
                <div class="border-b border-gray-100 pb-4">
                  <p
                    class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400"
                  >
                    Configuração
                  </p>
                  <h3 class="mt-2 text-lg font-semibold text-gray-900">
                    Dados do atendimento
                  </h3>
                  <p class="mt-1 text-sm text-gray-500">
                    Classifique o ticket para direcionar melhor a solicitação.
                  </p>
                </div>

                <div class="mt-5 space-y-4">
                  <div class="flex flex-col gap-1.5">
                    <label for="categoria" class="text-sm font-medium text-gray-500">
                      Categoria*
                    </label>
                    <div class="relative group">
                      <select
                        id="categoria"
                        v-model="form.categoria"
                        :disabled="isEditing"
                        :class="selectClass"
                      >
                        <option value="" disabled>Selecione uma categoria</option>
                        <option
                          v-for="categoria in categorias"
                          :key="categoria._id"
                          :value="categoria._id"
                        >
                          {{ categoria.nome }}
                        </option>
                      </select>

                      <svg
                        class="select-icon h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  <div class="flex flex-col gap-1.5">
                    <label for="prioridade" class="text-sm font-medium text-gray-500">
                      Prioridade*
                    </label>
                    <div class="relative group">
                      <select
                        id="prioridade"
                        v-model="form.prioridade"
                        :disabled="isEditing"
                        :class="selectClass"
                      >
                        <option value="" disabled>Selecione uma prioridade</option>
                        <option
                          v-for="prioridade in prioridades"
                          :key="prioridade._id"
                          :value="prioridade._id"
                        >
                          {{ prioridade.nome }}
                        </option>
                      </select>

                      <svg
                        class="select-icon h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div
                      class="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm"
                    >
                      <p
                        class="text-xs font-medium uppercase tracking-wider text-gray-400"
                      >
                        Número
                      </p>
                      <p class="mt-2 text-sm font-semibold text-gray-800">
                        {{ form.numero || "-" }}
                      </p>
                    </div>

                    <div
                      class="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm"
                    >
                      <p
                        class="text-xs font-medium uppercase tracking-wider text-gray-400"
                      >
                        Situação
                      </p>
                      <p class="mt-2 text-sm font-semibold text-gray-800">
                        {{ isEditing ? "Ticket existente" : "Novo ticket" }}
                      </p>
                    </div>
                  </div>

                  <div
                    class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-gray-500"
                  >
                    {{
                      isEditing
                        ? "Este ticket já foi criado. Você pode acompanhar ou adicionar comentários."
                        : "O ticket será criado e enviado para análise do suporte."
                    }}
                  </div>

                  <button
                    v-if="isEditing"
                    type="button"
                    class="h-11 w-full rounded-xl border border-blue-200 bg-blue-50 px-6 font-medium text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-100 hover:shadow-md"
                    @click="toggleComentario"
                  >
                    + Comentário
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showImputComentario && isEditing"
          ref="comentarioDiv"
          class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
        >
          <div class="border-b border-gray-100 px-6 py-5">
            <h2 class="text-lg font-semibold text-gray-900">Adicionar comentário</h2>
            <p class="mt-1 text-sm text-gray-500">
              Use esse campo para registrar uma atualização no ticket.
            </p>
          </div>

          <div class="space-y-4 p-6">
            <textarea
              v-model="newcomentario"
              rows="4"
              placeholder="Adicione um novo comentário"
              :class="textareaClass"
            />

            <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                class="h-11 rounded-xl border border-gray-200 bg-white px-6 font-medium text-gray-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-md"
                @click="toggleComentario"
              >
                Cancelar
              </button>

              <button
                type="button"
                class="h-11 rounded-xl px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                :style="{ backgroundColor: primaryColor }"
                @click="comment"
                @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                @mouseleave="$event.target.style.backgroundColor = primaryColor"
              >
                Salvar comentário
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="isEditing"
          class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
        >
          <div class="border-b border-gray-100 px-6 py-5">
            <h2 class="text-lg font-semibold text-gray-900">Comentários</h2>
            <p class="mt-1 text-sm text-gray-500">
              Histórico completo da conversa vinculada a este ticket.
            </p>
          </div>

          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50 text-gray-500">
                <tr>
                  <th class="px-6 py-4 text-left font-medium uppercase tracking-wider">
                    Data
                  </th>
                  <th class="px-6 py-4 text-left font-medium uppercase tracking-wider">
                    Remetente
                  </th>
                  <th class="px-6 py-4 text-left font-medium uppercase tracking-wider">
                    Comentário
                  </th>
                </tr>
              </thead>

              <tbody class="divide-y divide-gray-100 bg-white">
                <tr
                  v-for="comentario in comentarios.slice().reverse()"
                  :key="comentario._id"
                  :class="comentario.root ? 'bg-blue-50/60' : ''"
                >
                  <td class="whitespace-nowrap px-6 py-4 text-gray-600">
                    <span class="font-medium text-blue-700">
                      {{ moment(comentario.data).format("DD/MM HH:mm") }}
                    </span>
                  </td>

                  <td class="whitespace-nowrap px-6 py-4 text-gray-700">
                    {{
                      comentario.root
                        ? `Admin - ${comentario.userName}`
                        : `User - ${comentario.userName}`
                    }}
                  </td>

                  <td class="px-6 py-4 text-gray-700">
                    {{ comentario.conteudo }}
                  </td>
                </tr>

                <tr v-if="comentarios.length === 0">
                  <td colspan="3" class="px-6 py-10 text-center text-sm text-gray-400">
                    Ainda não há comentários neste ticket.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="!isEditing" class="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="submit"
            class="h-11 rounded-xl px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            :style="{ backgroundColor: primaryColor }"
            @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
            @mouseleave="$event.target.style.backgroundColor = primaryColor"
          >
            Salvar ticket
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  data() {
    return {
      moment,
      inputClass:
        "h-11 rounded-xl border border-gray-200 bg-white px-4 shadow-sm outline-none transition disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-100",
      selectClass:
        "h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white bg-none px-4 pr-10 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-gray-500",
      textareaClass:
        "w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm outline-none transition disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-100",
      categorias: [
        { nome: "Falha de envio massivo", _id: "Falha de envio massivo" },
        { nome: "Falha na iniciação manual", _id: "Falha na iniciação manual" },
        {
          nome: "Retorno não presentes na plataforma",
          _id: "Retorno não presentes na plataforma",
        },
        {
          nome: "Problemas de relatório ou status",
          _id: "Problemas de relatório ou status",
        },
        { nome: "Outros", _id: "Outros" },
      ],
      prioridades: [
        { nome: "Alta", _id: "Alta" },
        { nome: "Média", _id: "Média" },
        { nome: "Baixa", _id: "Baixa" },
      ],
      newcomentario: "",
      form: {
        titulo: "",
        descricao: "",
        categoria: "",
        prioridade: "",
        numero: "",
        comentarios: [],
      },
      showImputComentario: false,
    };
  },

  computed: {
    isEditing() {
      return !!this.form._id;
    },

    isFormValid() {
      return !!(
        String(this.form.titulo || "").trim() &&
        String(this.form.descricao || "").trim() &&
        String(this.form.categoria || "").trim() &&
        String(this.form.prioridade || "").trim()
      );
    },

    comentarios() {
      return Array.isArray(this.form.comentarios) ? this.form.comentarios : [];
    },
  },

  methods: {
    normalizeForm() {
      this.form.titulo = String(this.form.titulo || "").trim();
      this.form.descricao = String(this.form.descricao || "").trim();
      this.form.categoria = String(this.form.categoria || "").trim();
      this.form.prioridade = String(this.form.prioridade || "").trim();
    },

    async save() {
      if (!this.isFormValid) {
        this.$vToastify.error("Por favor, preencha todos os campos obrigatórios.");
        return;
      }

      this.normalizeForm();

      const method = this.form._id ? "put" : "post";
      const req = await this.$http[method]("/v1/ticket", this.form);

      if (req.data.success) {
        this.$vToastify.success("Salvo com sucesso!");
        this.$router.push({ path: "/suporte" });
        return;
      }

      this.$vToastify.error(req.data.err);
    },

    async comment() {
      const conteudo = String(this.newcomentario || "").trim();

      if (!conteudo) {
        this.$vToastify.error("Por favor, escreva algum comentário.");
        return;
      }

      const req = await this.$http.put(`/v1/ticket/${this.form._id}/comentario`, {
        conteudo,
      });

      if (req.data.success) {
        this.$vToastify.success("Salvo com sucesso!");

        const ticket = await this.$http.get(`/v1/ticket/${this.form._id}`);
        this.form = ticket.data;
        this.newcomentario = "";
        this.showImputComentario = false;
        return;
      }

      this.$vToastify.error(req.data.err);
    },

    toggleComentario() {
      this.showImputComentario = !this.showImputComentario;

      if (this.showImputComentario) {
        this.$nextTick(() => {
          this.$refs.comentarioDiv.scrollIntoView({ behavior: "smooth" });
        });
      }
    },
  },

  async beforeMount() {
    const id = this.$route.params.id;

    if (!id) return;

    const req = await this.$http.get(`/v1/ticket/${id}`);
    this.form = req.data;
  },
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  color: #3b82f6;
  transform: translateY(-50%) rotate(180deg);
}
</style>
